import { ReactNode, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Column, ColumnDef, ColumnFiltersState, OnChangeFn } from '@tanstack/react-table';
import { CheckCircle, ClockCountdown, NotePencil } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Skeleton } from '@/components/ui-kit/skeleton';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-media-query';
import { useInitialPageSize } from '@/hooks/use-server-table';
import { ColumnFilterValue, filterRowsByColumns } from '@/lib/table-query';
import { useGetAttendanceDays, useGetCorrectionStatusByDay } from '../../hooks/use-attendance';
import { AttendanceDayRow, isCorrectableDay } from '../../services/attendance-days';
import {
  formatAttendanceDay,
  formatDuration,
  formatShiftEnd,
  formatSignedDuration,
  formatTimeOfDay,
  getOrgDateString,
  isOpenRecord,
} from '../../services/attendance-rules';
import { AttendanceCorrection, AttendanceRecord } from '../../types/attendance.types';
import {
  DAY_STATUS_LABEL_KEY,
  DayDisplayStatus,
  DayStatusBadge,
} from '../day-status-badge/day-status-badge';
import { CorrectionStatusTag } from '../correction-status-tag/correction-status-tag';

type DayRow = AttendanceDayRow<AttendanceRecord>;

/** The statuses a day of the list can have (`NoRecord` is for lists of records only). */
const DAY_STATUSES: DayDisplayStatus[] = [
  'WFH',
  'WFO',
  'Working',
  'MissingClockOut',
  'Holiday',
  'Leave',
  'LeavePending',
  'Weekend',
  'Absent',
];

/** The day one month before `day` ("YYYY-MM-DD"). */
const monthBefore = (day: string): string => {
  const [year, month, date] = day.split('-').map(Number);
  const monthBack = new Date(Date.UTC(year, month - 2, date));
  // A shorter month clamps to its last day (31 Mar -> 28/29 Feb), never into the next month.
  const lastOfMonth = new Date(Date.UTC(year, month - 1, 0));
  return (monthBack > lastOfMonth ? lastOfMonth : monthBack).toISOString().slice(0, 10);
};

/** One month back to today on the organization's calendar, like the legacy list. */
const defaultDateFilter = (): ColumnFiltersState => {
  const today = getOrgDateString(new Date());
  return [{ id: 'AttendanceDate', value: { type: 'date', from: monthBefore(today), to: today } }];
};

/** The days to load for the Attendance Date filter: a missing end is today (or the start, when that
 * is later), a missing start one month before the end, and no filter the default month. */
const rangeOf = (filters: ColumnFiltersState): { startDate: string; endDate: string } => {
  const value = filters.find((filter) => filter.id === 'AttendanceDate')?.value as
    Extract<ColumnFilterValue, { type: 'date' | 'datetime' }> | undefined;
  const today = getOrgDateString(new Date());
  const endDate = value?.to || (value?.from && value.from > today ? value.from : today);
  return { startDate: value?.from || monthBefore(endDate), endDate };
};

/** Overtime / undertime keep the completed days that ran over or under their expected hours. */
const matchesOvertime = (row: DayRow, filter: ColumnFilterValue | undefined): boolean => {
  if (filter?.type !== 'options' || filter.values.length === 0) return true;
  const seconds = row.record?.OverTimeInSecond;
  if (seconds == null) return false;
  return (
    (filter.values.includes('overtime') && seconds > 0) ||
    (filter.values.includes('undertime') && seconds < 0)
  );
};

const durationOrDash = (value?: number | null) => (value == null ? '-' : formatDuration(value));

/** A day's correction request in place of its correction button: another correction of that day is
 * requested from the toolbar button (the workflow refuses it while one is pending). */
const CorrectionStatusIcon = ({
  status,
  t,
}: {
  status: 'Pending' | 'Approved';
  t: (key: string) => string;
}) => {
  const label = `${t('REQUEST_CORRECTION')}: ${t(status.toUpperCase())}`;
  const Icon = status === 'Pending' ? ClockCountdown : CheckCircle;
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn(
        'inline-flex size-8 items-center justify-center',
        status === 'Pending' ? 'text-warning' : 'text-success'
      )}
    >
      <Icon className="size-5" weight="fill" />
    </span>
  );
};

const createDayColumns = ({
  t,
  showBreak,
  showOvertime,
  onRequestCorrection,
  showCorrectionStatus = false,
  correctionStatusByDay,
  isCorrectionStatusLoading = false,
}: {
  t: (key: string) => string;
  showBreak: boolean;
  showOvertime: boolean;
  onRequestCorrection?: (row: DayRow) => void;
  showCorrectionStatus?: boolean;
  /** Each day's correction status; a rejected one offers the button again. */
  correctionStatusByDay?: Record<string, AttendanceCorrection['Status']>;
  isCorrectionStatusLoading?: boolean;
}): ColumnDef<DayRow>[] => [
  {
    id: 'AttendanceDate',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ATTENDANCE_DATE')} />,
    accessorFn: (row) => row.AttendanceDate,
    cell: ({ row }) => formatAttendanceDay(row.original.AttendanceDate),
    // Also the range loaded from the server (see `rangeOf`).
    meta: { filter: { type: 'date' } },
  },
  {
    id: 'StartTimeUtc',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_IN_TIME')} />,
    accessorFn: (row) => row.record?.StartTimeUtc ?? '',
    meta: { filter: { type: 'time' }, mobileOrder: 3 },
    cell: ({ row }) => {
      const record = row.original.record;
      return record?.StartTimeUtc
        ? formatTimeOfDay(record.StartTimeUtc, true, record.Timezone)
        : '-';
    },
  },
  {
    id: 'EndTimeUtc',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CLOCK_OUT_TIME')} />,
    // A day clocked in again keeps its last session's end until the new session ends.
    accessorFn: (row) =>
      row.record && !isOpenRecord(row.record) ? (row.record.EndTimeUtc ?? '') : '',
    meta: { filter: { type: 'time' }, mobileOrder: 4 },
    cell: ({ row }) => {
      const record = row.original.record;
      if (!record || isOpenRecord(record)) return '-';
      return formatShiftEnd(record.StartTimeUtc, record.EndTimeUtc, true, record.Timezone);
    },
  },
  ...(showBreak
    ? [
        {
          id: 'TotalBreakTimeInSecond',
          header: ({ column }: { column: Column<DayRow> }) => (
            <DataTableColumnHeader column={column} title={t('BREAK_TIME')} />
          ),
          accessorFn: (row: DayRow) => row.record?.TotalBreakTimeInSecond ?? null,
          cell: ({ row }: { row: { original: DayRow } }) =>
            durationOrDash(row.original.record?.TotalBreakTimeInSecond),
        },
      ]
    : []),
  {
    id: 'TotalWorkTimeInSecond',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('TOTAL_WORK_TIME')} />,
    accessorFn: (row) => row.record?.TotalWorkTimeInSecond ?? null,
    cell: ({ row }) => durationOrDash(row.original.record?.TotalWorkTimeInSecond),
    // Card: the first line is Total Work Time | Status, the second In Time | Out Time.
    meta: { mobileOrder: 1 },
  },
  ...(showOvertime
    ? [
        {
          id: 'OverTimeInSecond',
          header: ({ column }: { column: Column<DayRow> }) => (
            <DataTableColumnHeader column={column} title={t('OVERTIME')} />
          ),
          accessorFn: (row: DayRow) => row.record?.OverTimeInSecond ?? null,
          // Filtered by `matchesOvertime`, not by the value itself.
          meta: {
            filter: {
              type: 'options' as const,
              options: [
                { value: 'overtime', label: t('OVERTIME') },
                { value: 'undertime', label: t('UNDERTIME') },
              ],
            },
          },
          cell: ({ row }: { row: { original: DayRow } }) => {
            const seconds = row.original.record?.OverTimeInSecond;
            if (seconds == null) return '-';
            return (
              <span className={seconds < 0 ? 'text-destructive' : undefined}>
                {formatSignedDuration(seconds)}
              </span>
            );
          },
        },
      ]
    : []),
  {
    id: 'Status',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.status ?? '',
    meta: {
      mobile: 'body',
      mobileOrder: 2,
      filter: {
        type: 'options',
        options: DAY_STATUSES.map((status) => ({
          value: status,
          label: t(DAY_STATUS_LABEL_KEY[status]),
        })),
      },
    },
    cell: ({ row }) => (
      <DayStatusBadge
        status={row.original.status}
        label={row.original.label}
        forced={Boolean(row.original.record?.IsForceClockOut)}
      />
    ),
  },
  ...(onRequestCorrection || showCorrectionStatus
    ? [
        {
          id: 'actions',
          header: () => <span className="text-xs font-medium">{t('CORRECTION')}</span>,
          // Card: top right, next to the date.
          meta: { mobile: 'badge' as const },
          enableSorting: false,
          enableHiding: false,
          cell: ({ row }: { row: { original: DayRow } }) => {
            // Until the days' corrections are known, a pencil might turn into "pending" a moment later.
            if (isCorrectionStatusLoading) return <Skeleton className="size-6" />;
            const correction = correctionStatusByDay?.[row.original.day];
            // Read only (a reviewer): the day's correction as a tag, whatever its status.
            if (!onRequestCorrection) {
              return correction ? <CorrectionStatusTag status={correction} /> : null;
            }
            if (correction === 'Pending' || correction === 'Approved') {
              return <CorrectionStatusIcon status={correction} t={t} />;
            }
            return isCorrectableDay(row.original) ? (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('REQUEST_CORRECTION')}
                title={t('REQUEST_CORRECTION')}
                onClick={() => onRequestCorrection(row.original)}
              >
                <NotePencil className="size-5" />
              </Button>
            ) : null;
          },
        },
      ]
    : []),
];

/**
 * Every day of a picked range (one month back by default) of one employee's attendance, newest first,
 * like the legacy attendance list: each day shows where it was worked (WFH / WFO), or that it is a
 * holiday, leave, a day off or a missed working day (`buildAttendanceDays`). The Attendance Date
 * column filter picks the range, which is read whole, then paged, sorted and filtered in the browser.
 */
export const AttendanceDaysTable = ({
  userId,
  showBreak = true,
  showOvertime = false,
  onRequestCorrection,
  showCorrectionStatus = false,
  actions,
}: {
  userId: string;
  /** Break time column, for employees who can take breaks or lunch. */
  showBreak?: boolean;
  /** Overtime column (and its overtime/undertime filter), for employees overtime is on for. */
  showOvertime?: boolean;
  /** Offers a correction on each day that can be corrected. */
  onRequestCorrection?: (row: DayRow) => void;
  /** Without `onRequestCorrection`: each day's correction status only, as a tag (a reviewer). */
  showCorrectionStatus?: boolean;
  /** Shown above the table, on the right. */
  actions?: ReactNode;
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(defaultDateFilter);
  const initialPageSize = useInitialPageSize(15);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: initialPageSize });
  const { startDate, endDate } = rangeOf(columnFilters);

  const { data, isLoading, error } = useGetAttendanceDays(userId, startDate, endDate);
  const { data: correctionStatusByDay, isLoading: isCorrectionStatusLoading } =
    useGetCorrectionStatusByDay(
      userId,
      startDate,
      endDate,
      Boolean(onRequestCorrection || showCorrectionStatus)
    );

  const columns = useMemo(
    () =>
      createDayColumns({
        t,
        showBreak,
        showOvertime,
        onRequestCorrection,
        showCorrectionStatus,
        correctionStatusByDay,
        isCorrectionStatusLoading,
      }),
    [
      t,
      showBreak,
      showOvertime,
      onRequestCorrection,
      showCorrectionStatus,
      correctionStatusByDay,
      isCorrectionStatusLoading,
    ]
  );

  // Filtered here rather than by the table, so the pager counts the filtered days.
  const rows = useMemo(() => {
    const overtime = columnFilters.find((filter) => filter.id === 'OverTimeInSecond');
    const others = columnFilters.filter((filter) => filter.id !== 'OverTimeInSecond');
    return filterRowsByColumns(data ?? [], columns, others).filter((row) =>
      matchesOvertime(row, overtime?.value as ColumnFilterValue | undefined)
    );
  }, [data, columns, columnFilters]);

  const changeColumnFilters: OnChangeFn<ColumnFiltersState> = (updater) => {
    setColumnFilters(updater);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* On a phone the actions share the cards' filter line instead (see `mobileActions`). */}
      {actions && !isMobile && <div className="flex justify-end">{actions}</div>}
      <AdvanceDataTable
        data={rows}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{ ...pagination, totalCount: rows.length }}
        onPaginationChange={setPagination}
        manualFiltering
        columnFilters={columnFilters}
        onColumnFiltersChange={changeColumnFilters}
        getRowId={(row) => row.day}
        isExpandRowContent={false}
        // On a phone each day is a card: the date with its status chip, the times as pairs and the
        // correction button in the footer (the column defaults, see `MobileCardRole`).
        mobileView="cards"
        mobileActions={actions}
      />
    </div>
  );
};
